import {
  all, call, delay, put, select, takeLatest, take, fork, cancel,
} from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import {
  RootState,
  StartDivergenceResolutionPayload,
  ValidateManagerBadgePayload,
  ProductNotFoundPayload,
  StartItemResolutionPayload,
  ValidateProductPayload,
} from './DivergenceDemandsTypes'
import { selectCurrentUser } from './DivergenceDemandsSelector'
import { actions as appActions } from '../state'
import { toast } from 'react-toastify'
import { actions } from './DivergenceDemandsState'
import { handleConnectionError } from 'src/utils/checkNetworkStatus'
import apis from './DivergenceDemandsApi'
import { ReturnCall } from 'src/features/types'

function* startDivergenceResolution({ payload }: PayloadAction<StartDivergenceResolutionPayload>) {
  try {
    yield put(appActions.isLoading(true))

    const currentUser = yield select(selectCurrentUser)
    const payloadWithBadge = {
      ...payload,
      userBadge: currentUser?.badge,
    }

    const response: ReturnCall<typeof apis.startDivergenceResolution> = yield call(apis.startDivergenceResolution, payloadWithBadge)

    if (response.isOk) {
      yield put(actions.setProperty({ property: 'activeDivergenceItemId', value: response.data.id }))
      yield put(actions.setProductResolutionData(response.data))
    } else {
      const errorMessage = response?.data?.data?.message || 'Erro ao buscar item da divergências'
      toast.error(errorMessage)
    }
  } catch (error) {
    handleConnectionError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* productNotFound({ payload }: PayloadAction<ProductNotFoundPayload>) {
  const {
    divergenceId,
    onSuccess,
    onError,
  } = payload

  try {
    yield put(appActions.isLoading(true))

    const currentUser = yield select(selectCurrentUser)
    const response: ReturnCall<typeof apis.productNotFound> = yield call(apis.productNotFound, {
      divergenceId,
      userBadge: currentUser?.badge,
    })

    if (response.isOk) {
      toast.success('Divergencia finalizada com sucesso')
      onSuccess()
    } else {
      const errorMessage = response?.data?.data?.message || 'Erro ao registrar produto não encontrado'
      onError(errorMessage)
      toast.error(errorMessage)
    }
  } catch (error) {
    const errorMessage = 'Falha ao registrar produto não encontrado'
    onError(errorMessage)
    handleConnectionError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* startItemResolution({ payload }: PayloadAction<StartItemResolutionPayload>) {
  const {
    divergenceId,
    divergenceItemId,
    userBadge,
    locationAddress,
    countedQuantity,
    history,
    onSuccess,
    onError,
  } = payload

  try {
    yield put(appActions.isLoading(true))

    const response: ReturnCall<typeof apis.startItemResolution> = yield call(apis.startItemResolution, {
      divergenceId,
      divergenceItemId,
      userBadge,
      locationAddress,
      countedQuantity,
    })

    if (response.isOk) {
      if (!response.data) {
        onSuccess({ isFinished: true })
        return
      }

      yield put(actions.setProductResolutionData(response.data))
      yield put(actions.setProperty({ property: 'activeDivergenceItemId', value: response.data.id }))

      onSuccess({
        isFinished: false,
        data: response.data,
      })
    } else {
      if (response?.data?.status === 403 && response?.data?.data?.errorCode === 'UNAUTHORIZED_USER') {
        const unauthorizedMessage = response?.data?.data?.message || 'Usuário não autorizado.'
        toast.error(unauthorizedMessage)
        if (history) {
          history.push('/restocking/divergence')
        }

        return
      }

      const errorMessage = response?.data?.data?.message || 'Erro ao processar contagem'
      onError(errorMessage)
      toast.error(errorMessage)
    }
  } catch (error) {
    const errorMessage = 'Falha ao processar contagem'
    onError(errorMessage)
    handleConnectionError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* validateProduct({ payload }: PayloadAction<ValidateProductPayload>) {
  try {
    yield put(appActions.isLoading(true))
    const {
      divergenceId, prodBarcode, onSuccess, onError,
    } = payload

    const currentUser = yield select(selectCurrentUser)
    const response: ReturnCall<typeof apis.validateProduct> = yield call(
      apis.validateProduct,
      { divergenceId, prodBarcode, userBadge: currentUser?.badge },
    )

    if (response.isOk) {
      onSuccess(response.data)
    } else {
      onError(response?.data?.data?.message || 'Produto inválido')
    }
  } catch (error: any) {
    const errorMessage = error?.response?.data?.data?.message || 'Falha ao validar produto!'
    payload.onError(errorMessage)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateManagerBadge({ payload }: PayloadAction<ValidateManagerBadgePayload>) {
  const {
    badge, onSuccess, setError, onUserInUse,
  } = payload
  yield put(appActions.isLoading(true))

  try {
    if (handleConnectionError()) return

    const response: ReturnCall<typeof apis.validateManagerBadge> = yield call(
      apis.validateManagerBadge,
      payload,
    )

    if (!response.isOk) {
      setError(response?.data?.data?.message || 'Crachá inválido!')
      return
    }

    const { user, userId, hasUser } = response.data

    if (hasUser && onUserInUse && !payload.setUser) {
      if (hasUser.toLowerCase() !== user.toLowerCase()) {
        onUserInUse(hasUser)
        return
      }
    }

    yield put(actions.setProperty(
      {
        property: 'user',
        value: {
          user,
          userId,
          badge,
        },
      },
    ))
    onSuccess()
  } catch (error: any) {
    const errorMessage = error?.response?.data?.data?.message || 'Falha ao validar o crachá!'
    setError(errorMessage)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getDivergence(action: PayloadAction<{ filters?: any }>) {
  try {
    yield put(appActions.isLoading(true))
    const { filters } = action.payload || {}
    const response: ReturnCall<typeof apis.getDivergence> = yield call(apis.getDivergence, filters)

    if (response.isOk && response.data) {
      if (response.data.data && Array.isArray(response.data.data)) {
        yield put(actions.setDivergence({ divergence: response.data.data }))
      }

      if (response.data.pagination) {
        yield put(actions.setPagination(response.data.pagination))
      }
    } else {
      const errorMessage = response?.data?.message || 'Erro ao buscar divergências'
      toast.error(errorMessage)
    }
  } catch (error) {
    handleConnectionError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getDivergenceFilterOptions() {
  try {
    yield put(appActions.isLoading(true))
    const response: ReturnCall<typeof apis.getDivergenceFilterOptions> = yield call(apis.getDivergenceFilterOptions)

    if (response.isOk) {
      yield put(actions.setDivergenceFilterOptions({ filterOptions: response.data.data }))
    } else {
      const errorMessage = response?.data?.data?.message || 'Erro ao buscar opções de filtro'
      toast.error(errorMessage)
    }
  } catch (error) {
    handleConnectionError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* watchDivergencePolling() {
  let lastDataHash = ''
  const isActive = true

  while (isActive) {
    try {
      const state: RootState = yield select()
      const filters = state.divergenceDemands.divergenceFilters

      const result: ReturnCall<typeof apis.getDivergence> = yield call(apis.getDivergence, filters)

      if (result.isOk && result.data && Array.isArray(result.data)) {
        const dataHash = JSON.stringify(result.data)

        if (dataHash !== lastDataHash) {
          lastDataHash = dataHash
          yield put(actions.setDivergence({ divergence: result.data }))
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.data?.message || 'Erro ao buscar divergências'
      toast.error(errorMessage)
    }

    yield delay(60000)
  }
}

function* manageDivergencePolling() {
  let pollingTask = null

  while (true) {
    yield take(actions.startDivergencePolling.type)

    if (pollingTask) {
      yield cancel(pollingTask)
    }

    pollingTask = yield fork(watchDivergencePolling)
    yield take(actions.stopDivergencePolling.type)

    if (pollingTask) {
      yield cancel(pollingTask)
      pollingTask = null
    }
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getDivergence.type, getDivergence),
    takeLatest(actions.getDivergenceFilterOptions.type, getDivergenceFilterOptions),
    takeLatest(actions.validateManagerBadge.type, validateManagerBadge),
    takeLatest(actions.startDivergenceResolution.type, startDivergenceResolution),
    takeLatest(actions.validateProduct.type, validateProduct),
    takeLatest(actions.productNotFound.type, productNotFound),
    takeLatest(actions.startItemResolution.type, startItemResolution),
    fork(manageDivergencePolling),
  ])
}
